import { Button, IconButton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useEffect, useRef, useState } from 'react';
import { CommodityType, CompanyModel, CompanyProductAccessEntryModel, MarketViewApplication, MarketViewProductModel, ProductAccessOverrideType } from '../../../../Generated/Mark-It-View-Api';
import { MarkItViewApi } from '../../../../Apis/Apis';
import { StoneXRow } from '../../../StoneX';
import { StoneXModal, StoneXMultiAutocomplete, StoneXTextField } from '../../../StoneXMui';

const CommodityTypeValues = (Object.values(CommodityType) as CommodityType[]).filter(
  (commodityType) => commodityType !== CommodityType.Favorites,
);

interface UpdateCompanyModalProps {
  open: boolean;
  onClose: () => void;
  onCompanySaved: (company: CompanyModel, isNew: boolean) => void;
  company?: CompanyModel;
}

export default function UpdateCompanyModal(props: UpdateCompanyModalProps) {
  const { open, onClose, onCompanySaved, company } = props;

  const [name, setName] = useState<string>('');
  const [applications, setApplications] = useState<MarketViewApplication[]>([]);
  const [productAccess, setProductAccess] = useState<CompanyProductAccessEntryModel[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [productsByType, setProductsByType] = useState<Record<string, MarketViewProductModel[]>>({});
  const [loadingTypes, setLoadingTypes] = useState<Set<string>>(new Set());
  const [limitProductAccess, setLimitProductAccess] = useState<boolean>(false);
  const loadedTypesRef = useRef<Set<string>>(new Set());

  const actionLabel = company ? 'Update' : 'Add';

  useEffect(() => {
    setName(company?.name ?? '');
    setApplications(company?.marketViewApplications ?? []);
    setProductAccess(company?.productAccess ?? []);
    setIsSaving(false);
    setProductsByType({});
    setLoadingTypes(new Set());
    setLimitProductAccess((company?.productAccess ?? []).length > 0);
    loadedTypesRef.current = new Set();

    // Load products for existing commodity types
    for (const entry of (company?.productAccess ?? [])) {
      if (entry.commodityType) {
        loadProductsForType(entry.commodityType);
      }
    }
  }, [company]);

  function loadProductsForType(commodityType: CommodityType) {
    if (loadedTypesRef.current.has(commodityType)) {
      return;
    }
    loadedTypesRef.current.add(commodityType);
    setLoadingTypes((prev) => {
      const next = new Set(prev);
      next.add(commodityType);
      return next;
    });
    MarkItViewApi.listMarketViewProducts(null, commodityType).then((response) => {
      setProductsByType((previous) => ({ ...previous, [commodityType]: response.data.rows ?? [] }));
      setLoadingTypes((prev) => {
        const next = new Set(prev);
        next.delete(commodityType);
        return next;
      });
    });
  }

  function removeProductAccessEntry(index: number) {
    setProductAccess(productAccess.filter((_, i) => i !== index));
  }

  function updateProductAccessEntry(index: number, updated: Partial<CompanyProductAccessEntryModel>) {
    setProductAccess(productAccess.map((entry, i) => (i === index ? { ...entry, ...updated } : entry)));
  }

  function handleCommodityTypesChange(selectedTypes: CommodityType[]) {
    const existing = productAccess.filter((entry) => entry.commodityType && selectedTypes.includes(entry.commodityType));
    const newTypes = selectedTypes.filter((type) => !productAccess.some((entry) => entry.commodityType === type));
    const newEntries = newTypes.map((type) => ({ commodityType: type }));
    setProductAccess([...existing, ...newEntries] as CompanyProductAccessEntryModel[]);

    for (const type of newTypes) {
      loadProductsForType(type);
    }
  }

  function saveCompany() {
    if (isSaving) {
      return;
    }
    setIsSaving(true);

    const accessToSave = limitProductAccess ? productAccess : [];

    const promise = company
      ? MarkItViewApi.updateCompany(null, company.companyGuid, {
          companyGuid: company.companyGuid,
          name,
          marketViewApplications: applications,
          productAccess: accessToSave,
        })
      : MarkItViewApi.addCompany(null, {
          name,
          marketViewApplications: applications,
          productAccess: accessToSave,
        });

    promise
      .then((res) => {
        if (res.data.company) {
          onCompanySaved(res.data.company, !company);
        }
      })
      .finally(() => {
        setIsSaving(false);
      });
  }

  const selectedCommodityTypes = productAccess
    .map((entry) => entry.commodityType)
    .filter((commodityType): commodityType is CommodityType => commodityType != null);

  return (
    <StoneXModal header={`${actionLabel} Company`} open={open} onClose={onClose} width="800px">
      <StoneXRow align="start">
        <StoneXTextField label="Company Name" value={name} onUpdate={setName} />
      </StoneXRow>

      <StoneXRow align="start">
        <StoneXMultiAutocomplete
          width="520px"
          label="Applications"
          options={Object.values(MarketViewApplication)}
          getOptionLabel={(x) => x}
          value={applications}
          onUpdate={setApplications}
        />
      </StoneXRow>

      {!limitProductAccess && (
        <StoneXRow align="start">
          <Button variant="outlined" size="small" onClick={() => setLimitProductAccess(true)}>
            Limit Product Access
          </Button>
          <Typography variant="body2" color="textSecondary" style={{ marginLeft: 8, alignSelf: 'center' }}>
            Currently has full access to all products.
          </Typography>
        </StoneXRow>
      )}

      {limitProductAccess && (
        <>
          <StoneXRow align="start">
            <h3 style={{ margin: '8px 0' }}>Product Access</h3>
            <Button
              variant="text"
              size="small"
              color="error"
              onClick={() => { setLimitProductAccess(false); setProductAccess([]); }}
              style={{ marginLeft: 8 }}
            >
              Remove Limits
            </Button>
          </StoneXRow>

          <StoneXRow align="start">
            <StoneXMultiAutocomplete
              width="520px"
              label="Commodity Types"
              options={CommodityTypeValues}
              getOptionLabel={(x) => x}
              value={selectedCommodityTypes}
              onUpdate={(values) => handleCommodityTypesChange(values as CommodityType[])}
            />
          </StoneXRow>

          <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
            <strong>None:</strong> Full access to all products in the commodity type.{' '}
            <strong>Allow:</strong> Only selected products are accessible — new products will be blocked until explicitly added.{' '}
            <strong>Block:</strong> All products are accessible except those selected — new products will be automatically available.
          </Typography>

          {productAccess.map((entry, index) => (
            <div key={entry.commodityType ?? index} style={{ border: '1px solid #e0e0e0', borderRadius: 4, padding: 12, marginBottom: 8 }}>
              <StoneXRow align="start">
                <h4 style={{ margin: 0 }}>{entry.commodityType}</h4>
                <IconButton size="small" onClick={() => removeProductAccessEntry(index)}>
                  <DeleteIcon />
                </IconButton>
              </StoneXRow>

              <StoneXRow align="start">
                <div style={{ display: 'inline-block' }}>
                  <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Access Rule</label>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={entry.overrideType ?? 'None'}
                    onChange={(_event, value) => {
                      if (value === null) {
                        return;
                      }
                      const overrideType = value === 'None' ? undefined : (value as ProductAccessOverrideType);
                      updateProductAccessEntry(index, { overrideType, productIds: overrideType ? entry.productIds : null });
                    }}
                  >
                    <ToggleButton value="None">None</ToggleButton>
                    <ToggleButton value="Allow">Allow</ToggleButton>
                    <ToggleButton value="Block">Block</ToggleButton>
                  </ToggleButtonGroup>
                </div>
                {entry.overrideType && (
                  <>
                    {entry.commodityType && loadingTypes.has(entry.commodityType) && (
                      <Typography variant="body2" color="textSecondary">Loading products...</Typography>
                    )}
                    {(!entry.commodityType || !loadingTypes.has(entry.commodityType)) && (
                      <StoneXMultiAutocomplete
                        width="350px"
                        label="Products"
                        options={entry.commodityType ? (productsByType[entry.commodityType] ?? []) : []}
                        getOptionLabel={(product) => `${product.name} (${product.productId})`}
                        value={(entry.commodityType ? (productsByType[entry.commodityType] ?? []) : []).filter((p) => entry.productIds?.includes(p.productId))}
                        onUpdate={(selected) => {
                          updateProductAccessEntry(index, { productIds: selected.map((p) => p.productId) });
                        }}
                      />
                    )}
                  </>
                )}
              </StoneXRow>
            </div>
          ))}
        </>
      )}

      <StoneXRow justify="end">
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={saveCompany} disabled={isSaving}>{`${actionLabel} Company`}</Button>
      </StoneXRow>
    </StoneXModal>
  );
}
